import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, TrendingUp, BarChart3, Activity, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function GuidePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="pt-20"> {/* Add padding top to account for the absolute header if needed, but MainLayout is absolute top. */}
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('stock.backToHome')}
        </Button>

        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-8 text-center">
            {t('index.howItWorksTitle')}
          </h1>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <GuideCard
              icon={<TrendingUp className="h-8 w-8 text-blue-600" />}
              title={t('index.trend')}
              description={t('index.trendDescription')}
            />
            <GuideCard
              icon={<BarChart3 className="h-8 w-8 text-green-600" />}
              title={t('index.volume')}
              description={t('index.volumeDescription')}
            />
            <GuideCard
              icon={<Activity className="h-8 w-8 text-purple-600" />}
              title={t('index.momentum')}
              description={t('index.momentumDescription')}
            />
            <GuideCard
              icon={<Zap className="h-8 w-8 text-orange-600" />}
              title={t('index.volatility')}
              description={t('index.volatilityDescription')}
            />
          </div>

          {/* Detailed Info Section */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="space-y-6 text-slate-600">
              <p className="text-lg leading-relaxed">
                {t('index.howItWorksIntro')}
              </p>
              
              <div className="grid grid-cols-1 gap-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{t('index.trend')}</h3>
                  <p>{t('index.howItWorksTrend')}</p>
                </div>
                
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{t('index.volume')}</h3>
                  <p>{t('index.howItWorksVolume')}</p>
                </div>
                
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{t('index.momentum')}</h3>
                  <p>{t('index.howItWorksMomentum')}</p>
                </div>
                
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{t('index.volatility')}</h3>
                  <p>{t('index.howItWorksVolatility')}</p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-100">
                <p className="text-lg font-medium text-slate-900">
                  {t('index.howItWorksSummary')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GuideCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-slate-100">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-2 bg-slate-50 rounded-lg">{icon}</div>
        <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-slate-600 leading-relaxed">{description}</p>
    </div>
  );
}
